import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@repo/ui";
import {
  AlertTriangle,
  XCircle,
  Clock,
  CheckCircle2,
  ShieldAlert,
  CreditCard,
  ArrowRight,
  Bell,
} from "lucide-react";
import type {
  AlertType,
  AlertCategory,
  AlertSeverity,
} from "@/lib/types/alerts";

export interface AlertCardProps {
  id: string;
  alertKey: string;
  type: AlertType;
  category: AlertCategory;
  severity: AlertSeverity;
  title: string;
  message: string;
  itemName?: string;
  ndc?: string;
  timestamp: string;
  actionUrl: string;
  actionLabel: string;
  onDismiss?: (alertKey: string) => void;
  onRemind?: () => void;
}

const categoryIcon: Record<
  AlertCategory,
  React.ComponentType<{ className?: string }>
> = {
  compliance: ShieldAlert,
  expiration: Clock,
  stock: AlertTriangle,
  subscription: CreditCard,
};

const typeIconOverride: Partial<
  Record<AlertType, React.ComponentType<{ className?: string }>>
> = {
  sold_out: XCircle,
};

const severityConfig: Record<
  AlertSeverity,
  { label: string; dot: string; text: string }
> = {
  critical: {
    label: "Critical",
    dot: "bg-red-500",
    text: "text-red-600",
  },
  high: {
    label: "High",
    dot: "bg-orange-500",
    text: "text-orange-600",
  },
  medium: {
    label: "Medium",
    dot: "bg-amber-500",
    text: "text-amber-600",
  },
  low: {
    label: "Info",
    dot: "bg-muted-foreground",
    text: "text-muted-foreground",
  },
};

export function AlertCard({
  alertKey,
  type,
  category,
  severity,
  title,
  message,
  itemName,
  ndc,
  timestamp,
  actionUrl,
  actionLabel,
  onDismiss,
  onRemind,
}: AlertCardProps) {
  const Icon = typeIconOverride[type] || categoryIcon[category];
  const sev = severityConfig[severity];

  return (
    <div
      className="group rounded-xl border border-border/60 bg-surface px-4 py-3.5 transition-colors hover:bg-muted/20"
      data-testid={`alert-card-${alertKey}`}
    >
      <div className="flex items-start gap-3">
        {/* Icon — monochrome, no background */}
        <Icon className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />

        {/* Content */}
        <div className="min-w-0 flex-1">
          {/* Title row */}
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold truncate">{title}</h4>
            <span className="flex items-center gap-1.5 shrink-0">
              <span className={`h-1.5 w-1.5 rounded-full ${sev.dot}`} />
              <span className={`text-[11px] font-medium ${sev.text}`}>
                {sev.label}
              </span>
            </span>
          </div>

          {/* Item info */}
          {(itemName || ndc) && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {itemName && <span className="font-medium">{itemName}</span>}
              {itemName && ndc && <span> &middot; </span>}
              {ndc && <span className="font-mono text-[11px]">{ndc}</span>}
            </p>
          )}

          {/* Message */}
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
            {message}
          </p>

          {/* Actions row */}
          <div className="flex items-center gap-1.5 mt-2.5">
            {type !== "sold_out" && type !== "reorder_needed" && (
              <Button
                size="sm"
                variant="outline"
                asChild
                className="h-7 text-xs px-2.5"
              >
                <Link to={actionUrl}>
                  {actionLabel}
                  <ArrowRight className="ml-1 h-3 w-3" />
                </Link>
              </Button>
            )}
            {onRemind && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onRemind}
                className="h-7 text-xs px-2 text-muted-foreground hover:text-foreground"
                title="Remind me about this"
              >
                <Bell className="h-3.5 w-3.5" />
              </Button>
            )}
            <span className="ml-auto text-[11px] text-muted-foreground/70 whitespace-nowrap">
              {new Date(timestamp).toLocaleDateString()}
            </span>
            {onDismiss && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onDismiss(alertKey)}
                className="h-7 text-xs px-2 text-muted-foreground hover:text-foreground"
                title="Dismiss"
                data-testid={`alert-dismiss-${alertKey}`}
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                <span className="ml-1">Dismiss</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
